import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import ConfirmModal from '../../components/ConfirmModal';
import StatusBadge from '../../components/StatusBadge';
import API, { resolvePhoto } from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { ArrowLeft, CheckCircle, MessageSquare, X, Upload, FileText, Download } from 'lucide-react';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import InvoiceModal from '../../components/InvoiceModal';
import PriceTag from '../../components/PriceTag';
import './ArticleReview.css';

export default function ArticleReview() {
  const { id: articleId } = useParams();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionNote, setRevisionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [senderName, setSenderName] = useState('');
  const [showInvoice, setShowInvoice] = useState(false);
  // Bukti dipilih tapi BELUM dikirim (pratinjau lokal, masih bisa diganti)
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState('');

  // Pilih / ganti file — hanya pratinjau lokal, tidak langsung kirim
  const pickProof = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('File harus berupa gambar.'); return; }
    if (proofPreview) URL.revokeObjectURL(proofPreview);
    setProofFile(file);
    setProofPreview(URL.createObjectURL(file));
  };

  const clearProof = () => {
    if (proofPreview) URL.revokeObjectURL(proofPreview);
    setProofFile(null);
    setProofPreview('');
  };

  // Kirim bukti yang sudah dipratinjau ke server
  const submitProof = async () => {
    if (!proofFile) return;
    setUploadingProof(true);
    try {
      const fd = new FormData();
      fd.append('proof', proofFile);
      fd.append('senderName', senderName);
      await API.post(`/payments/manual/${articleId}/proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Bukti transfer terkirim. Menunggu verifikasi admin.');
      clearProof();
      window.location.reload();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal mengunggah bukti'); }
    finally { setUploadingProof(false); }
  };

  const hasInvoice = article?.payment?.invoiceNumber && article?.payment?.status === 'paid';

  useEffect(() => {
    API.get(`/articles/my-articles/${articleId}/preview`).then(r => { setArticle(r.data.article); setLoading(false); }).catch(() => setLoading(false));
  }, [articleId]);

  const handleApprove = async () => {
    setConfirmApprove(false);
    setSubmitting(true);
    try {
      await API.patch(`/articles/my-articles/${articleId}/decision`, { decision: 'approve' });
      toast.success('Artikel disetujui dan siap dipublikasikan!');
      navigate('/artikel-saya');
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal menyetujui'); }
    finally { setSubmitting(false); }
  };

  const handleRevision = async () => {
    if (!revisionNote.trim()) { toast.error('Catatan revisi wajib diisi'); return; }
    setSubmitting(true);
    try {
      await API.patch(`/articles/my-articles/${articleId}/decision`, { decision: 'revise', notes: revisionNote });
      toast.success('Catatan revisi dikirim ke editor');
      navigate('/artikel-saya');
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal mengirim revisi'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <DashboardLayout title="Detail Artikel">
      <div className="ki-card">{[...Array(5)].map((_, i) => <div key={i} className="shimmer" style={{ height: 40, borderRadius: 8, marginBottom: 12 }} />)}</div>
    </DashboardLayout>
  );

  if (!article) return (
    <DashboardLayout title="Detail Artikel">
      <div className="ki-card" style={{ textAlign: 'center', padding: '60px 24px' }}>
        <div style={{ color: 'var(--ki-text-muted)', marginBottom: 16 }}>Artikel tidak ditemukan.</div>
        <Link to="/artikel-saya" className="btn btn-secondary">Kembali</Link>
      </div>
    </DashboardLayout>
  );

  const canReview = article.status === 'review_reader';
  const hasContent = article.title && article.content;
  const readerRevisionCount = (article.revisions || []).filter(r => r.from === 'reader').length;
  const canRevise = canReview && readerRevisionCount < 1;

  const waitForSnap = () => new Promise((resolve, reject) => {
    if (window.snap) return resolve();
    let tries = 0;
    const iv = setInterval(() => {
      if (window.snap) { clearInterval(iv); resolve(); }
      else if (++tries > 20) { clearInterval(iv); reject(new Error('Snap timeout')); }
    }, 300);
  });

  const handlePay = async () => {
    setSubmitting(true);
    try {
      await waitForSnap();
      const payRes = await API.post('/payments/create', { articleId });
      const snapToken = payRes.data.snapToken;
      setSubmitting(false);
      window.snap.pay(snapToken, {
        onSuccess: () => { toast.success('Pembayaran berhasil!'); window.location.reload(); },
        onPending: () => { toast('Pembayaran tertunda, cek email Anda.', { icon: '⏳' }); },
        onError:   () => { toast.error('Pembayaran gagal, coba lagi.'); },
        onClose:   () => { toast('Pembayaran dibatalkan.', { icon: '⚠️' }); },
      });
    } catch (err) {
      setSubmitting(false);
      toast.error(err.response?.data?.message || 'Gagal membuka pembayaran');
    }
  };

  return (
    <DashboardLayout title="Detail Artikel" subtitle="Pantau status dan konten artikel Anda">
      <Link to="/artikel-saya" className="btn btn-ghost btn-sm" style={{ marginBottom: 20, display: 'inline-flex' }}>
        <ArrowLeft size={14} /> Kembali ke Daftar
      </Link>

      <div className="dash-two-col">
        {/* Main content */}
        <div>
          {/* Info yang Anda Ajukan */}
          <div className="ki-card" style={{ marginBottom: 20 }}>
            <div className="section-title" style={{ marginBottom: 16 }}>Informasi yang Anda Ajukan</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                { label: 'Siapa (Who)', val: article.who },
                { label: 'Apa (What)', val: article.what },
                { label: 'Kapan (When)', val: article.when },
                { label: 'Di mana (Where)', val: article.where },
                { label: 'Mengapa (Why)', val: article.why },
                { label: 'Bagaimana (How)', val: article.how },
              ].map(f => (
                <div key={f.label} style={{ gridColumn: ['Mengapa (Why)', 'Bagaimana (How)'].includes(f.label) ? 'span 2' : '' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--ki-text-faint)', marginBottom: 4 }}>{f.label}</div>
                  <div style={{ fontSize: 14, color: 'var(--ki-text-muted)' }}>{f.val || '-'}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hasil artikel yang ditulis editor */}
          {hasContent && (
            <div className="ki-card" style={{ marginBottom: 20 }}>
              <div className="section-title" style={{ marginBottom: 16 }}>Artikel yang Ditulis Editor</div>
              {article.photos?.find((_, i) => i === article.featuredPhoto) && (
                <img src={resolvePhoto(article.photos[article.featuredPhoto].url)} alt={article.title} style={{ width: '100%', maxHeight: 320, objectFit: 'cover', borderRadius: 10, marginBottom: 16 }} />
              )}
              <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>{article.title}</h2>
              {article.excerpt && <p style={{ fontSize: 16, color: 'var(--ki-text-muted)', fontStyle: 'italic', marginBottom: 16 }}>{article.excerpt}</p>}
              <div className="article-body" dangerouslySetInnerHTML={{ __html: article.content }} />
            </div>
          )}


          {/* Action panel for review_reader */}
          {canReview && hasContent && (
            <div className="ki-card" style={{ border: '1px solid rgba(0,21,64,0.25)', background: 'rgba(0,21,64,0.04)' }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Tindakan Diperlukan</div>
              <p style={{ fontSize: 14, color: 'var(--ki-text-muted)', marginBottom: 20 }}>
                Artikel Anda telah selesai ditulis. Harap baca dengan teliti, lalu setujui atau minta revisi.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={submitting} onClick={() => setConfirmApprove(true)}>
                  <CheckCircle size={15} /> Setujui
                </button>
                {canRevise ? (
                  <button className="btn btn-secondary" style={{ flex: 1 }} disabled={submitting} onClick={() => setShowRevisionModal(true)}>
                    <MessageSquare size={15} /> Minta Revisi
                  </button>
                ) : (
                  <div style={{ flex: 1, fontSize: 12, color: 'var(--ki-text-faint)', display: 'flex', alignItems: 'center', fontStyle: 'italic' }}>
                    Jatah revisi sudah digunakan
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar status */}
        <div className="ki-card" style={{ position: 'sticky', top: 80 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Status Artikel</div>
          <div style={{ marginBottom: 12 }}>
            <StatusBadge status={article.status} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--ki-text-faint)' }}>
            <div style={{ marginBottom: 6 }}>Kategori: <span style={{ color: 'var(--ki-text-muted)' }}>{article.category?.name || '-'}</span></div>
            <div style={{ marginBottom: 6 }}>Editor: <span style={{ color: 'var(--ki-text-muted)' }}>{article.assignedEditor?.name || 'Belum ditugaskan'}</span></div>
            <div>Diajukan: <span style={{ color: 'var(--ki-text-muted)' }}>{format(new Date(article.createdAt), 'd MMM yyyy', { locale: id })}</span></div>
          </div>
          {article.status === 'payment_pending' && settings.paymentMode === 'manual' && (
            <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--in-bg-3)', border: '1px solid var(--in-border)', borderRadius: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Pembayaran Transfer Manual</div>
              {article.payment?.proofUrl ? (
                <div style={{ fontSize: 13, color: 'var(--in-text-muted)', lineHeight: 1.6 }}>
                  ✅ Bukti transfer telah dikirim. Menunggu verifikasi admin.
                  {article.payment?.note && <div style={{ marginTop: 6, color: 'var(--in-danger)' }}>{article.payment.note}</div>}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: 'var(--in-text-muted)', lineHeight: 1.7, marginBottom: 12 }}>
                    Transfer <PriceTag amount={article.payment?.amount || settings.paymentAmount || 0} /> ke:
                    <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--in-card)', borderRadius: 8, border: '1px solid var(--in-border)' }}>
                      <div style={{ fontWeight: 700, color: 'var(--in-text)' }}>{settings.bankName}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--in-blue)', letterSpacing: 0.5 }}>{settings.bankAccountNo}</div>
                      <div style={{ fontSize: 12 }}>a.n. {settings.bankAccountName}</div>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12 }}>{settings.paymentInstruction}</div>
                  </div>
                  <input className="form-input" placeholder="Nama pengirim transfer (opsional)" value={senderName} onChange={e => setSenderName(e.target.value)} style={{ marginBottom: 10 }} />

                  {!proofFile ? (
                    /* Belum pilih file */
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', display: 'inline-flex', gap: 6 }}>
                      <Upload size={14} /> Pilih Bukti Transfer
                      <input type="file" accept="image/*" hidden onChange={e => pickProof(e.target.files[0])} />
                    </label>
                  ) : (
                    /* Sudah pilih — pratinjau, belum dikirim. Bisa ganti / hapus / kirim */
                    <div>
                      <div style={{ position: 'relative', display: 'inline-block', marginBottom: 10 }}>
                        <img src={proofPreview} alt="Pratinjau bukti" style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, border: '1px solid var(--in-border)', display: 'block' }} />
                        <span style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6 }}>Belum dikirim</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--in-text-faint)', marginBottom: 10, wordBreak: 'break-all' }}>{proofFile.name}</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button className="btn btn-primary btn-sm" disabled={uploadingProof} onClick={submitProof} style={{ display: 'inline-flex', gap: 6 }}>
                          <Upload size={14} /> {uploadingProof ? 'Mengirim...' : 'Kirim Bukti'}
                        </button>
                        <label className="btn btn-secondary btn-sm" style={{ cursor: uploadingProof ? 'not-allowed' : 'pointer', display: 'inline-flex', gap: 6, opacity: uploadingProof ? 0.6 : 1 }}>
                          Ganti File
                          <input type="file" accept="image/*" hidden disabled={uploadingProof} onChange={e => pickProof(e.target.files[0])} />
                        </label>
                        <button className="btn btn-sm" disabled={uploadingProof} onClick={clearProof}
                          style={{ background: 'none', border: '1px solid var(--in-border)', color: 'var(--in-text-muted)' }}>
                          Batal
                        </button>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--in-text-faint)', marginTop: 8 }}>
                        Bukti belum dikirim. Anda masih bisa mengganti file sebelum menekan &ldquo;Kirim Bukti&rdquo;.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {article.status === 'payment_pending' && settings.paymentMode !== 'manual' && (
            <div style={{ marginTop: 16, padding: '12px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>Menunggu Pembayaran</div>
              <div style={{ fontSize: 12, color: '#92400E', lineHeight: 1.5 }}>
                Kembali ke daftar artikel dan klik <strong>Bayar Sekarang</strong> untuk menyelesaikan pembayaran.
              </div>
            </div>
          )}
          {hasInvoice && (
            <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--in-bg-3)', border: '1px solid var(--in-border)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(5,150,105,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', flexShrink: 0 }}>
                  <FileText size={20} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>Invoice {article.payment.invoiceNumber}</div>
                  <div style={{ fontSize: 12, color: 'var(--in-text-muted)' }}>
                    Lunas · Rp {Number(article.payment.amount || 0).toLocaleString('id-ID')}
                    {article.payment.paidAt && ` · ${format(new Date(article.payment.paidAt), 'd MMM yyyy', { locale: id })}`}
                  </div>
                </div>
              </div>
              <button onClick={() => setShowInvoice(true)} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', gap: 6 }}>
                <Download size={14} /> Lihat / Unduh Invoice
              </button>
            </div>
          )}
          {['payment_confirmed', 'writing', 'review_producer', 'revision_editor'].includes(article.status) && (
            <div style={{ marginTop: 16, padding: '12px 14px', background: 'rgba(0,74,159,0.06)', border: '1px solid rgba(0,74,159,0.2)', borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--in-blue)', marginBottom: 4 }}>Sedang Dikerjakan</div>
              <div style={{ fontSize: 12, color: 'var(--in-text-muted)', lineHeight: 1.6 }}>
                Pembayaran telah dikonfirmasi. Tim editor kami sedang menulis dan menyunting artikel Anda. Anda akan menerima notifikasi saat artikel siap untuk Anda tinjau.
              </div>
            </div>
          )}
          {article.status === 'deletion_requested' && (
            <div style={{ marginTop: 14, padding: '10px 12px', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, fontSize: 12, color: '#DC2626', lineHeight: 1.5 }}>
              Pengajuan penghapusan sedang ditinjau oleh admin. Artikel masih dapat dibaca hingga admin mengonfirmasi.
            </div>
          )}
          {article.photos?.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ki-text-faint)', marginBottom: 8 }}>Foto Anda</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {article.photos.map((ph, i) => (
                  <img key={i} src={resolvePhoto(ph.url)} alt={ph.caption} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 6 }} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirmApprove}
        title="Setujui Artikel"
        message="Anda menyetujui artikel ini untuk dipublikasikan. Artikel akan masuk antrian publikasi."
        confirmLabel="Setujui"
        onConfirm={handleApprove}
        onCancel={() => setConfirmApprove(false)}
        loading={submitting}
      />

      {/* Revision Modal */}
      {showRevisionModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowRevisionModal(false)}>
          <div className="modal-box">
            <div className="modal-head">
              <div style={{ fontSize: 17, fontWeight: 700 }}>Catatan Revisi</div>
              <button className="btn btn-icon btn-ghost" onClick={() => setShowRevisionModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: 'var(--ki-text-muted)', marginBottom: 16 }}>Jelaskan bagian mana yang perlu diperbaiki:</p>
              <div className="form-group">
                <textarea className="form-textarea" rows={5} placeholder="Contoh: Tolong perbaiki paragraf pembuka, tambahkan kutipan dari narasumber, dan perbaiki ejaan pada bagian ke-3..." value={revisionNote} onChange={e => setRevisionNote(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setShowRevisionModal(false)}>Batal</button>
                <button className="btn btn-primary" disabled={submitting || !revisionNote.trim()} onClick={handleRevision}>{submitting ? 'Mengirim...' : 'Kirim Catatan Revisi'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InvoiceModal open={showInvoice} onClose={() => setShowInvoice(false)} article={article} />
    </DashboardLayout>
  );
}
